import { useEffect, useState, useRef, Fragment, createRef } from "react";
import Button from "../components/Button";
import { ReactComponent as IconArrow } from "../img/icons/arrow-right.svg";
import { ReactComponent as IconEdit } from "../img/icons/edit.svg";

import InfosQuestionnaire from "./InfosQuestionnaire";
import Checkbox from "../components/Checkbox";
import useIncrementSimulationStep from "../hooks/useIncrementSimulationStep";
import TextReassu from "../components/TextReassu";

const Choices = ({ question, set_valid_step, set_answer }) => {
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [selectedSubChoice, setSelectedSubChoice] = useState(null);
  const [displayMessage, setDisplayMessage] = useState(false);
  const [displayMessageType, setDisplayMessageType] = useState(null);
  const [subchoices, setSubChoices] = useState(null);

  useEffect(() => {
    if (selectedChoices.length > 0)
    {
      for (var i = 0; i < selectedChoices.length; i++)
      {
        const selectedChoice = selectedChoices[i];
        if (question.choices[selectedChoice].exit) {
          setDisplayMessage(question.choices[selectedChoice].exit);
          setDisplayMessageType('info');
          set_valid_step(false);
          return;
        } else {
          if (question.choices[selectedChoice].info)
          {
            setDisplayMessage(question.choices[selectedChoice].info);
            setDisplayMessageType('info');
          } else
            setDisplayMessage(false);
        }
      }
      set_valid_step(true);
      const answers = selectedChoices.map((choice) => 
        question.choices[choice].key ?? question.choices[choice].label
      );
      if (selectedSubChoice && subchoices)
        answers.push([subchoices.label] + ' : ' + selectedSubChoice);
      set_answer((question.multi || selectedSubChoice) ? answers : answers[0]);
    }else
      set_valid_step(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChoices, selectedSubChoice]);

  const setSelectedChoice = (value) => {
    if (question.multi) {
      const idx = selectedChoices.indexOf(value);
      setSelectedChoices(idx === -1 ? [...selectedChoices, value] : selectedChoices.filter(v => v !== value));
    } else {
      setSelectedChoices([value]);
      setSubChoices(question.choices[value]?.subchoices);
    }
  };

  return (
    <>
      <div className="choices">
        {
          question.choices.map((choice, index) => {
            const hasImage = choice.icon || choice.image;
            return (
              <div 
                className={'choice' + (selectedChoices.lastIndexOf(index) !== -1 ? ' selected' : '') + (hasImage ? ' image' : '')} 
                key={choice.label} 
                onClick={() => { setSelectedChoice(index); }}
              >
                {hasImage && <div className="imgwrapper">{choice.icon ? <choice.icon /> : (choice.image && <img alt="" src={choice.image} />)}</div>}
                {choice.label && <label>{choice.label}</label>}
              </div>
            );
          })
        }
      </div>
      {subchoices && 
        <span className="subchoice">{ subchoices.label }
        {
          subchoices.choices.map((choice, index) => {
            return (
              <Checkbox key={choice} checked={selectedSubChoice === choice} set_checked={() => setSelectedSubChoice(choice)}>{ choice }</Checkbox>
            )
          })
        }
        </span>
      }
      {question.multi && <span className="multichoice">😎 Vous pouvez en sélectionner plusieurs !</span>}
      {displayMessage && <div className={displayMessageType} dangerouslySetInnerHTML={{ __html: displayMessage }} />}
    </>
  )
};

const InputNumber = ({ question, set_valid_step, set_answer }) => {
  const [displayMessage, setDisplayMessage] = useState(false);
  const [displayMessageType, setDisplayMessageType] = useState(null);

  const inputField = useRef(null);

  const updateInputValue = () => {
    const val = inputField.current.value;

    if (question.max && (Number(val) > question.max)) {
      setDisplayMessage(question.exit);
      setDisplayMessageType('info');
      set_valid_step(false);
    } else if (val.match(/^[0-9]+$/)) {
      setDisplayMessage(false);
      set_valid_step(true);
      set_answer(val);
    } else {
      setDisplayMessage("<b>Oups !</b> Veuillez saisir un nombre valide");
      setDisplayMessageType('error')
      set_valid_step(false);
    }
  };

  return (
    <>
    <input type="number" className="number" placeholder={question.placeholder} onChange={ () => updateInputValue() } ref={inputField}/>
    {displayMessage && <div className={displayMessageType} dangerouslySetInnerHTML={{ __html: displayMessage }} />}
    </>
  )
};

const MultiNumber = ({ question, set_valid_step, set_answer }) => {
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [otherSelected, setOtherSelected] = useState(null);
  const [displayMessage, setDisplayMessage] = useState(false);
  const [displayMessageType, setDisplayMessageType] = useState(null);

  const inputRefs = question.choices.map(() => createRef(null));

  const updateInputValue = () => {
    var nb_valid = 0;
    var nb_invalid = 0;
    var inputs = {};

    question.choices.forEach((choice, index) => {
      const val = inputRefs[index].current.value;
      if (val.match(/^[0-9]+$/)) {
        nb_valid++;
        inputs[choice.key ?? choice.label] = val;
      } else if (val !== '') {
        nb_invalid++;
      }
    });
    if (selectedChoice !== null)
      nb_valid++;

    if ((nb_valid + nb_invalid) > 1)
    {
      setDisplayMessage("<b>Oups !</b> Veuillez ne saisir qu'une seule valeur");
      setDisplayMessageType('error')
      set_valid_step(false);
    } else if (nb_invalid > 0) {
      setDisplayMessage("<b>Oups !</b> Veuillez saisir un nombre valide");
      setDisplayMessageType('error')
      set_valid_step(false);
    } else if (nb_valid === 1) {
      setDisplayMessage(false);
      set_valid_step(true);
      set_answer(inputs);
    } else if ((nb_valid + nb_invalid) === 0) {
      setDisplayMessage(false);
      set_valid_step(false);
    }
  };

  const toggleOther = () => {
    setOtherSelected(!otherSelected);
  };

  useEffect(() => {
    if (!otherSelected) setSelectedChoice(null);
    if (otherSelected !== null)
      updateInputValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherSelected])

  useEffect(() => {
    updateInputValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChoice])

  return (
    <div className="choices multinumber">
      {
        question.choices.map((choice, index) => {
            return (
              <Fragment key={index}>
                { index > 0 && <span>ou</span> }
                <div className="choicewrapper">
                  <span>{choice.label}</span>
                  <div className="choice addend">
                      <input type="text" placeholder={question.placeholder} onChange={() => updateInputValue()} ref={inputRefs[index]} />
                      <span>{choice.addend}</span>
                  </div>
                </div>
              </Fragment>
            );
        })
      }
      {
        question.alternate ?
          <Fragment>
            <div className={'choice' + (otherSelected ? ' selected' : '')} onClick={ toggleOther }>
              <label>{question.alternate.label}</label>
            </div>
            { otherSelected ? 
            <div className="alternate">
              {
                question.alternate.choices.map((subchoice, subindex) => {
                  return (
                    <div 
                      className={'choice' + (selectedChoice === subindex ? ' selected' : '')} 
                      key={subchoice.label} 
                      onClick={() => { setSelectedChoice(subindex); }}
                    >
                      <label>{subchoice.label}</label>
                    </div>  
                  )  
                })
              }
            </div> :
            <></>
            }
          </Fragment>
          :<></>
      }
      { displayMessage && <div className={displayMessageType} dangerouslySetInnerHTML={{ __html: displayMessage }} /> }
    </div>
  )
};

const Question = ({ question, set_valid_step, set_answer }) => {
  switch (question.type) {
    case 'choice':
      return (
        <Choices question={question} set_valid_step={set_valid_step} set_answer={set_answer} />
      );
    case 'number':
      return (
        <InputNumber question={question} set_valid_step={set_valid_step} set_answer={set_answer} />
      );
    case 'multinumber':
      return (
        <MultiNumber question={question} set_valid_step={set_valid_step} set_answer={set_answer} />
      );
    default:
      return (<></>);
  }
};

const QuestionnaireStep = ({ question, open, nextstep, setstep, setanswer }) => {
  const [validStep, setValidStep] = useState(false);
  const [boxChecked, setBoxChecked] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(false);
  const [displayMessageType, setDisplayMessageType] = useState(null);

  const validateStep = (answer) => {
    if (validStep) {
      if (question.checkbox && !boxChecked && question.checkbox.error) {
        setDisplayMessage(question.checkbox.error);
        setDisplayMessageType('error');
      } else { 
        question.answered = true;
        nextstep();
      }
    }
  };

  const updateValidStep = (valid) => {
    setValidStep(valid);
    if (!valid)
      question.answered = false;
  }

  const toggleCheckbox = () => {
    setBoxChecked(!boxChecked);
  }

  useEffect(() => {
    if (boxChecked)
      setDisplayMessage(false);
  }, [boxChecked]);

  return (
    <div className={'questionnaire-step ' + (open ? 'open ' : '') + (question.answered ? 'answered ' : '')}>
      { open && question.header && <div className="overheader">{question.header}</div> }
      <div className="header">
        <div className="subheader" onClick={() => { if (question.answered || (process.env?.NODE_ENV === 'development')) setstep(); }}>
          <div className="title">{question.title}</div>
          {question.answered && <div className="edit"><span>Modifier</span><IconEdit /></div>}
        </div>
      </div>
      <div className="question">
        <Question question={question} set_valid_step={updateValidStep} set_answer={setanswer} />
        {question.checkbox && <Checkbox set_checked={ toggleCheckbox } checked={ boxChecked }>{ question.checkbox.label }</Checkbox>}
        {displayMessage && <div className={displayMessageType} dangerouslySetInnerHTML={{ __html: displayMessage }} />}
        <Button variant={'wide white' + (validStep ? '' : ' outlined thin light')} onclick={validateStep}>Valider mon choix</Button>
      </div>
    </div>
  )
};

const InfosStep = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState({});
  const {simParams, incrementSimulationStep} = useIncrementSimulationStep();

  const nextstep = () => {
    setActiveStep(Math.min(activeStep + 1, InfosQuestionnaire.length));
  };

  const qstr = JSON.stringify(InfosQuestionnaire);

  useEffect(() => {
    var nb_answered = 0;
    InfosQuestionnaire.forEach((q) => {
      if (q.answered)
        nb_answered++;
    });
    if (nb_answered === InfosQuestionnaire.length)
      setCompleted(true);
  }, [qstr]);

  const setAnswer = (answer) => {
    setAnswers({...answers, [InfosQuestionnaire[activeStep].key]: answer});
  }

  const validateInfos = () => {
    if (completed)
    {
      window.scrollTo(0,0);
      incrementSimulationStep({...simParams, infos: answers});
    }
  };

  /*useEffect(() => {
    console.log(answers);
  }, [answers]);*/

  return (
    <>
      <div className="left" id="questionnaire">
        {
          InfosQuestionnaire.map((question, index) => {
            return (
              <QuestionnaireStep
                key={question.key}
                question={question}
                open={activeStep === index}
                nextstep={nextstep}
                setstep={() => { setActiveStep(index) }}
                setanswer={setAnswer}
              />
            );
          })
        }
        <div className="finished">
          <span>
          Merci ! <br/>
          vous êtes trop fort 💪
          </span>
          <Button
            height={60}
            fontSize={18}
            IconAfter={IconArrow}
            variant={completed ? '' : ' outlined thin light'}
            link={completed ? '/simulateur/resultats' : null}
            onclick={validateInfos}
          >
            Valider mes informations
          </Button>
          <TextReassu/>
        </div>
      </div>
      <div className="right">
        <div className="text">
          <h2>Vos informations pour une étude ☀️ sur-mesure !</h2>
          <span>Pas de panique cela ne prend que quelques secondes !</span>
        </div>
        <Button
          height={60}
          fontSize={18}
          IconAfter={IconArrow}
          variant={completed ? '' : ' outlined thin light'}
          link={completed ? '/simulateur/resultats' : null}
          onclick={validateInfos}
        >
          Valider mes informations
        </Button>
        <TextReassu/>
      </div>
    </>
  )
};

export default InfosStep;